import { useDashboardSummary } from '../hooks/useDashboardSummary';
import { emptyTypeBreakdown } from '@overlayqa/shared';
import { useMinimumLoading } from '../hooks/useMinimumLoading';
import { ExtensionInstallCard } from '../components/dashboard/ExtensionInstallCard';
import {
  NeedsAttentionList,
  NeedsAttentionListSkeleton,
} from '../components/dashboard/NeedsAttentionList';
import {
  RecentActivitySection,
  RecentActivitySkeleton,
} from '../components/dashboard/RecentActivitySection';
import {
  QuickStatsSection,
  QuickStatsSkeleton,
} from '../components/dashboard/QuickStatsSection';
import {
  IssueTypeBars,
  IssueTypeBarsSkeleton,
} from '../components/dashboard/IssueTypeBars';
import {
  RecentProjectsSection,
  RecentProjectsSkeleton,
  RECENT_PROJECTS_LIMIT,
} from '../components/dashboard/RecentProjectsSection';
import { log } from '@/lib/logger';
import { usePendingExtensionOpen } from '../hooks/usePendingExtensionOpen';
import { ExtensionInstallToast } from '../components/dashboard/ExtensionInstallToast';
import { useAppSession } from '../hooks/useAppSession';
import { useProjects } from '../hooks/useProjects';
import { LoadError } from '../components/shared/IssuesLoadError';

export function DashboardPage() {
  const { data, isLoading: summaryLoading, error: summaryError, refetch } = useDashboardSummary();
  // My Projects renders the Projects page's own list (same query key as
  // ProjectsPage, so the cache is shared): the newest RECENT_PROJECTS_LIMIT.
  const { data: appSession } = useAppSession();
  const { data: projects, isLoading: projectsLoading, error: projectsError, refetch: refetchProjects } = useProjects(
    appSession?.activeTeamId,
    { includePreviews: true, includeCounts: true },
  );
  const recentProjects = [...(projects ?? [])]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, RECENT_PROJECTS_LIMIT);
  const rawLoading = summaryLoading || (projectsLoading && !projects);
  const error = summaryError ?? projectsError;
  const hasCachedContent = !!data && !!projects;
  const isLoading = useMinimumLoading(rawLoading, 300);
  const { hasPendingOpen, pendingUrl, extensionDetected, openPendingSite, error: extensionError } = usePendingExtensionOpen();

  log.info('[DashboardPage] render', { isLoading, rawLoading, hasData: data?.hasData, error: !!error });

  // Defensive defaults — production API may not have breakdown fields yet
  const EMPTY_TYPE = emptyTypeBreakdown();
  const EMPTY_STATUS = { open: 0, 'in-progress': 0, resolved: 0, verified: 0, closed: 0 };
  const EMPTY_RESOLUTION = { avgDays: 0, prevAvgDays: 0, sparkline: [] as (number | null)[] };
  const summary = data ? {
    ...data,
    typeBreakdown: { ...EMPTY_TYPE, ...(data.typeBreakdown ?? {}) },
    typeBreakdownByRange: {
      week: { ...EMPTY_TYPE, ...(data.typeBreakdownByRange?.week ?? {}) },
      month: { ...EMPTY_TYPE, ...(data.typeBreakdownByRange?.month ?? {}) },
      quarter: { ...EMPTY_TYPE, ...(data.typeBreakdownByRange?.quarter ?? {}) },
    },
    statusBreakdown: { ...EMPTY_STATUS, ...(data.statusBreakdown ?? {}) },
    resolutionTime: data.resolutionTime ?? EMPTY_RESOLUTION,
    needsAttentionIssues: data.needsAttentionIssues ?? [],
    stats: {
      total: data.stats?.total ?? 0,
      open: data.stats?.open ?? 0,
      closed: data.stats?.closed ?? 0,
      thisWeek: data.stats?.thisWeek ?? 0,
      lastWeek: data.stats?.lastWeek ?? 0,
      thisMonth: data.stats?.thisMonth ?? 0,
      lastMonth: data.stats?.lastMonth ?? 0,
    },
  } : null;
  // Sample projects are onboarding examples, not the user's first project.
  // Keep real data visible even when it belongs to a single project.
  const gettingStarted = summary?.stats.total === 0 && (projects ?? []).filter(project => !project.isDemo).length <= 1;

  return (
    <section id="dashboard" className="page-content">
      <div>
        <div className="space-y-5">
          {hasPendingOpen ? <ExtensionInstallToast url={pendingUrl ?? undefined} extensionDetected={extensionDetected} onOpenSite={openPendingSite} error={extensionError} /> : <ExtensionInstallCard />}

          {error && !isLoading && (
            <LoadError resource="dashboard data" error={error} onRetry={() => Promise.all([refetch(), refetchProjects()]).then(results => ({ isError: results.some(result => result.isError) }))} />
          )}

          {(isLoading || (error && !hasCachedContent)) && (
            <>
            {isLoading && <p role="status" className="sr-only">Loading dashboard...</p>}
            <div className={`space-y-5 ${isLoading ? 'skeleton-fade-in' : '[&_*]:animate-none'}`} aria-hidden="true">
              <QuickStatsSkeleton />
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)] gap-5">
                <div className="card-sm">
                  <IssueTypeBarsSkeleton variant="overview" />
                </div>
                <div className="card-sm">
                  <NeedsAttentionListSkeleton variant="overview" />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <div className="lg:col-span-8">
                  <RecentProjectsSkeleton />
                </div>
                <div className="lg:col-span-4">
                  <RecentActivitySkeleton variant="overview" />
                </div>
              </div>
            </div>
            </>
          )}

          {!isLoading && (!error || hasCachedContent) && summary && (
            <div className="data-fade-in space-y-5">
              {gettingStarted ? <RecentProjectsSection items={recentProjects} gettingStarted /> : <>
              <QuickStatsSection stats={summary.stats} />
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)] gap-5">
                <div className="card-sm">
                  <IssueTypeBars variant="overview" data={summary.typeBreakdown} />
                </div>
                <div className="card-sm">
                  <NeedsAttentionList variant="overview" items={summary.needsAttentionIssues} />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <div className="lg:col-span-8 [&>*]:h-full">
                  <RecentProjectsSection items={recentProjects} />
                </div>
                <div className="lg:col-span-4 [&>*]:h-full">
                  <RecentActivitySection variant="overview" items={summary.recentActivity} />
                </div>
              </div>
              </>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
